import React from "react";
import { StaticQuery, graphql, Link } from "gatsby";
import PropTypes from "prop-types";
import Utils from "../../utils";

const createMenuHierarchy = (menuData, menuName) => {
  let tree = [],
    mappedArr = {},
    arrElem,
    mappedElem;

  // First map the nodes of the array to an object -> create a hash table.
  for (let i = 0, len = menuData.length; i < len; i++) {
    arrElem = menuData[i].node;
    if (arrElem.menu_name === menuName && arrElem.enabled === true) {
      mappedArr[arrElem.drupal_id] = arrElem;
      if (
        arrElem.drupal_parent_menu_item != null &&
        arrElem.drupal_parent_menu_item.includes(arrElem?.bundle)
      ) {
        let stripped_drupal_id = arrElem.drupal_parent_menu_item.replace(
          arrElem?.bundle + ":",
          ""
        );
        mappedArr[arrElem.drupal_id].drupal_parent_menu_item =
          stripped_drupal_id;
      }
      mappedArr[arrElem.drupal_id]["children"] = [];
    }
  }

  // Create an array to hold the parent elements
  const parents = [];

  for (let id in mappedArr) {
    if (mappedArr.hasOwnProperty(id)) {
      mappedElem = mappedArr[id];
      // If the element is not at the root level, add it to its parent array of children.
      if (mappedElem.drupal_parent_menu_item) {
        if (mappedArr[mappedElem.drupal_parent_menu_item]) {
          if (
            !mappedArr[mappedElem.drupal_parent_menu_item].hasOwnProperty(
              "children"
            )
          ) {
            mappedArr[mappedElem.drupal_parent_menu_item]["children"] = [];
          }
          mappedArr[mappedElem.drupal_parent_menu_item]["children"].push(
            mappedElem
          );
        }
      }
      // If the element is at the root level, add it to the first level elements array.
      else {
        parents.push(mappedElem);
      }
    }
  }

  // Sort the parent elements array by the weight field
  parents.sort((a, b) => a.weight - b.weight);

  // Recursively sort the children arrays based on the weight field
  const sortChildrenByWeight = (element) => {
    if (element.children.length > 0) {
      element.children.sort((a, b) => a.weight - b.weight);
      element.children.forEach((child) => {
        sortChildrenByWeight(child);
      });
    }
  };

  parents.forEach((element) => {
    sortChildrenByWeight(element);
  });

  return parents;
};

const buildLink = (link, dropdownItem) => {
  if (link?.url && Utils.isExternal(link.url)) {
    if (link?.title) {
      return (
        <a href={link.url} className={"external nav-link"} target="_blank">
          {link.title}
        </a>
      );
    }
  } else if (!link?.external && link?.url) {
    if (link?.title) {
      return (
        <Link
          className={dropdownItem ? dropdownItem : "nav-link"}
          activeClassName="active"
          to={link.url.replace("/ca", "")}
          partiallyActive={true}
        >
          {link.title}
        </Link>
      );
    }
  } else if (!link?.external && link?.url?.includes("internal:")) {
    if (link?.title) {
      return (
        <Link
          className={dropdownItem ? dropdownItem : "nav-link"}
          activeClassName="active"
          to={link.url.replace("internal:", "")}
          partiallyActive={true}
        >
          {link.title}
        </Link>
      );
    }
  } else {
    if (link?.title && link?.url) {
      return (
        <a href={link.url} className={"external nav-link"}>
          {link.title}
        </a>
      );
    }
  }
};

const buildMenu = (menuArray, itemClass, location) => {
  if (!menuArray) {
    return;
  }
  const pathname = location?.pathname;
  let menu = [];
  for (let item in menuArray) {
    const isActiveDropdown = itemClass.includes("active");

    if (menuArray[item]?.children?.length !== 0) {
      if (menuArray[item]?.drupal_id && menuArray[item]?.url) {
        const liActive = getUrlActive(menuArray[item], pathname);
        menu.push(
          <li
            key={menuArray[item].drupal_id}
            className={` ${
              liActive ? "active-item" : ""
            } nav-item dropdown`}
          >
            {buildLink(
              menuArray[item],
              isActiveDropdown
                ? "nav-link dropdown-toggle active"
                : "nav-link dropdown-toggle"
            )}
            <div
              className={`dropdown-menu ${
                isActiveDropdown ? "active" : ""
              }`}
              aria-labelledby="navbarDropdown"
            >
              {buildSubMenu(menuArray[item].children, "dropdown-menu")}
            </div>
          </li>
        );
      }
    } else {
      const liActive = getUrlActive(menuArray[item], pathname);
      if (menuArray[item]?.url) {
        menu.push(
          <li
            className={`${liActive ? "active-item" : ""} nav-item`}
            key={menuArray[item].drupal_id}
          >
            {buildLink(menuArray[item], "nav-link")}
          </li>
        );
      }
    }
  }

  return menu;
};

const buildSubMenu = (menuArray, dropdownMenu) => {
  if (!menuArray) {
    return;
  }
  let menu = [];
  for (let item in menuArray) {
    menu.push(
      <span key={menuArray[item].drupal_id}>
        {buildLink(menuArray[item], "dropdown-item")}
      </span>
    );
  }

  return menu;
};

const generateMenu = (menuLinks, menuName, itemClass, location) => {
  let menu;
  menu = createMenuHierarchy(
    menuLinks.allMenuLinkContentMenuLinkContent.edges,
    menuName
  );
  menu = buildMenu(menu, itemClass, location);

  return menu;
};

const getUrlActive = (item, location) => {
  const tmpPathname = item?.url?.replace("/ca", "");
  const tmpPathname1 = removeSlash(location);
  if (tmpPathname === tmpPathname1) {
    return true;
  }
  return false;
};

const removeSlash = (url) => {
  return url?.replace(/\/$/, "");
};

const Menu = ({ menuName, itemClass, path, url, location }) => (
  <StaticQuery
    query={graphql`
      query MenuQuery {
        allMenuLinkContentMenuLinkContent {
          edges {
            node {
              enabled
              title
              expanded
              url
              drupal_parent_menu_item
              drupal_id
              menu_name
              weight
            }
          }
        }
      }
    `}
    render={(data) => (
      <ul className="navbar-nav mr-auto">
        {generateMenu(data, menuName, itemClass, location)}
      </ul>
    )}
  />
);

Menu.propTypes = {
  menuName: PropTypes.string,
};

Menu.defaultProps = {
  menuName: `goel-navegacio-principal`,
  itemClass: ``,
};

export default Menu;
